import React from 'react';
import { Helmet } from 'react-helmet';
import DetailsHeaderText from '../components/DetailsHeaderText';
import DetailsText from '../components/DetailsText';
import DetailsList from '../components/DetailsList';
import SectionDetailsLink from '../components/SectionDetailsLink';
import { sectionIds } from './utils';
import LeadText from '../components/LeadText';
import TextWithImageSection from '../components/TextWithImageSection';
import replayImage3 from '../assets/images/replay/replay_3.png';
import replayImage4 from '../assets/images/replay/replay_4.png';
import replayImage5 from '../assets/images/replay/replay_5.png';
import replayImage6 from '../assets/images/replay/replay_6.png';
import replayImage7 from '../assets/images/replay/replay_7.png';
import replayImage8 from '../assets/images/replay/replay_8.png';
import replayImage9 from '../assets/images/replay/replay_9.png';
import replayImage10 from '../assets/images/replay/replay_10.png';
import Text from '../components/Text';
import Title from '../components/Title';
import DividerTitle from '../components/DividerTitle';
import Media from '../components/Media';
import Paragraph from '../components/Paragraph';
import SectionTitle from '../components/SectionTitle';
import Subtitle from '../components/Subtitle';
import PageFooterSlope from './PageFooterSlope';

const ReplayDetailsContent = () => (
    <>
        <Helmet>
            <title>realCity | replay – Traffic Analysis & Visualization system</title>
            <meta
                name="description"
                content="From historical trip data to detailed visual reports: improve service quality, verify operations, and optimize workflows with realCity | replay."
            />
            <meta
                name="keywords"
                content="historical traffic data analysis, public transport performance review, route efficiency analysis, data-driven traffic optimization, interactive transport visualization"
            />
        </Helmet>
        <DetailsHeaderText
            category="Products"
            title="realCity | replay"
        />
        <TextWithImageSection
            introductorySection
            followedByDetailsText
            text={(
                <>
                    <Subtitle>Public transport data analysis & visualization system</Subtitle>
                    <Text>
                        <LeadText>
                            Turn historical trip data into clear, actionable insights with realCity | replay, your
                            all-in-one platform for traffic analysis and visualization.
                        </LeadText>
                        Investigate passenger complaints, uncover inefficiencies, and verify operational records in just a
                        few clicks. Empower your team to improve service quality and optimize daily workflows with
                        confidence.
                    </Text>
                    <SectionDetailsLink navigateTo={`/#${sectionIds.contactId}`}>
                        See a demo first-hand!
                    </SectionDetailsLink>
                </>
            )}
            image={(
                <Media
                    component="img"
                    src={replayImage10}
                    variant="left"
                    alt="Replay public transport data analysis & visualization system"
                />
            )}
            alignment="image-right"
        />
        <DetailsText>
            <SectionTitle>
                A public transport software your team will actually like to use
            </SectionTitle>
            <Paragraph>
                We designed realCity | replay to solve real-world challenges faced by public transport
                professionals.
                Built around user feedback, our platform focuses on what matters most: delivering clarity, speed,
                and
                control with straightforward features. The result is a traffic analysis and visualization tool that
                your
                team will find genuinely useful in their everyday work.
            </Paragraph>
            <Paragraph>
                <DetailsList subtitle="Our clients use this traffic analysis and data visualization tool to:">
                    <li>Investigate passenger complaints quickly and accurately,</li>
                    <li>Optimize timetables and schedules using real historical data,</li>
                    <li>Review and verify duty sheets with historical insights,</li>
                    <li>Comply with authority inquiries in cases of accidents,</li>
                    <li>Detect inefficient operations via map and timeline views,</li>
                    <li>Enhance e-bus efficiency with energy and battery analysis</li>
                </DetailsList>
            </Paragraph>
            <SectionTitle>
                Here’s a closer look at how the platform works in action
            </SectionTitle>
        </DetailsText>
        <TextWithImageSection
            text={(
                <>
                    <Title> Dashboard Widgets</Title>
                    <Text>
                        Get real-time insights at a glance! Customize your dashboard with your most-used widgets from
                        saved searches to key metrics like trip delays, driver stats, or vehicle performance. Filter by
                        route, date, or time to focus on the moments that matter.
                    </Text>
                </>
            )}
            image={(
                <Media
                    component="img"
                    src={replayImage3}
                    variant="right"
                    alt="Replay Dashboard Widgets"
                />
            )}
            alignment="image-left"
            last
        />
        <DividerTitle>Track Your Fleet Like Never Before</DividerTitle>
        <TextWithImageSection
            text={(
                <>
                    <Title>Advanced Search</Title>
                    <Text>
                        Go deep with both text- and map-based queries. Search by license plate, street name, driver
                        ID, stop name, or route number. Whether you’re investigating a complaint or verifying a trip
                        detail, this tool gives you fast, precise results in just a few clicks.
                    </Text>
                </>
            )}
            image={(
                <Media
                    component="img"
                    src={replayImage4}
                    variant="left"
                    alt="Replay Advanced Search"
                />
            )}
            alignment="image-right"
        />
        <TextWithImageSection
            text={(
                <>
                    <Title>Map View</Title>
                    <Text>
                        Visualize traffic data with precision. Select an area on the interactive map to see which
                        vehicles and drivers passed through, whether they stopped, how long they stayed, and how fast
                        they moved. Ideal for identifying bottlenecks or service gaps.
                    </Text>
                </>
            )}
            image={(
                <Media
                    component="img"
                    src={replayImage5}
                    variant="right"
                    alt="Replay Map view"
                />
            )}
            alignment="image-left"
        />
        <TextWithImageSection
            text={(
                <>
                    <Title>Timeline View</Title>
                    <Text>
                        See the full picture over time. Analyze detailed trip timelines with insights like door
                        open/close events, audio announcements, passenger boarding counts, alerts, and more. All
                        segmented by vehicle, driver, or trip.
                    </Text>
                </>
            )}
            image={(
                <Media
                    component="img"
                    src={replayImage6}
                    variant="left"
                    alt="Replay Timeline View"
                />
            )}
            alignment="image-right"
        />
        <DividerTitle>Make Public Transport Data Make Sense</DividerTitle>
        <TextWithImageSection
            text={(
                <>
                    <Title>Stop Analytics</Title>
                    <Text>
                        Understand performance at stop level. Compare how different vehicles perform on the same route,
                        spot recurring delays, and identify root causes like increased passenger load or excessive wait
                        times.
                    </Text>
                </>
            )}
            image={(
                <Media
                    component="img"
                    src={replayImage7}
                    variant="right"
                    alt="Replay Stop Analytics"
                />
            )}
            alignment="image-left"
            first
        />
        <TextWithImageSection
            text={(
                <>
                    <Title>Detailed Reports</Title>
                    <Text>
                        Export insights in seconds. Create detailed or summarized, downloadable reports for audits,
                        internal reviews, or planning. Perfect for management briefings or regulatory compliance.
                    </Text>
                </>
            )}
            image={(
                <Media
                    component="img"
                    src={replayImage8}
                    variant="left"
                    alt="Replay Detailed Reports"
                />
            )}
            alignment="image-right"
        />
        <TextWithImageSection
            text={(
                <>
                    <Title>E-bus Monitoring</Title>
                    <Text>
                        Track electric vehicle efficiency. With real-time battery and energy usage data, your team can
                        optimize charging cycles, monitor energy consumption, and feed predictive tools with accurate
                        performance metrics.
                    </Text>
                </>
            )}
            image={(
                <Media
                    component="img"
                    src={replayImage9}
                    variant="right"
                    alt="Replay E-bus Monitoring"
                />
            )}
            alignment="image-left"
        />
        <DetailsText>
            <SectionTitle>
                Take a closer look at the tech behind realCity | replay
            </SectionTitle>
            <Paragraph>
                <DetailsList>
                    <li>Cloud-based infrastructure for simple deployment and scalability.</li>
                    <li>Easily integratable with external data sources.</li>
                    <li>Customizable dashboard and a wide range of filter options.</li>
                    <li>Effective data visualization based on historical data.</li>
                </DetailsList>
            </Paragraph>
            <SectionTitle>
                Curious to see it in action? Let us walk you through it in a live demo.
            </SectionTitle>
            <Paragraph>
                Book a free, personalized demo and discover how your team can use our transit data analytics platform to
                solve real challenges.
            </Paragraph>
            <SectionDetailsLink navigateTo={`/#${sectionIds.contactId}`}>
                Request Demo
            </SectionDetailsLink>
        </DetailsText>
        <PageFooterSlope />
    </>
);

export default ReplayDetailsContent;
